import { Image, Stack } from '@mantine/core'
import { IconUserStar } from '@tabler/icons-react'
import { z } from 'zod'
import RingProgressCard from '../../../../stories/RingProgressCard/RingProgressCard'
import { useAllPlayers } from '../../../../players/api'
import { useAllTeams } from '../../../../teams/api'
import { Metric, useMetrics, useMetricsData } from '../../../../widgets/api'
import { Fmt, FmtColored, useFmtColor } from '../../../../widgets/format'
import { DashboardWidget } from './types'
import { useResolvedContext } from './useResolvedContext'
import {
  ColorSchemaFields,
  MetricSelect,
  NEUTRAL_COLOR,
  NeutralColorsToggle,
  PeriodFields,
  PeriodSchemaFields,
  StatisticSelect,
  SubjectFields,
  TitleInput,
} from './OptionsFormFields'
import { WidgetEmpty, WidgetSuspense } from './WidgetShell'
import classes from './KpiSpotlight.module.css'

const RING_SIZE_PX = 200

const Schema = z.object({
  subject_type: z.enum(['user', 'team']).default('user'),
  subject_id: z.number().default(0),
  ...PeriodSchemaFields,
  ...ColorSchemaFields,
  title: z.string().default(''),
  metric_key: z.string().default(''),
  statistic: z.enum(['value', 'value_ytd']).default('value'),
})

type Settings = z.infer<typeof Schema>

type Subject = { name: string; photo_url: string }

function useSubject(
  subject_type: 'user' | 'team',
  subject_id: number,
  competition_id: number
): Subject | null {
  const { data: playersResp } = useAllPlayers({ competition_id })
  const { data: teamsResp } = useAllTeams({ competition_id })

  if (subject_id <= 0) return null

  if (subject_type === 'user') {
    const p = playersResp.items.find((x) => x.user_id === subject_id)
    if (!p) return null
    return { name: p.name, photo_url: p.photo.thumbnail }
  }

  const t = teamsResp.items.find((x) => x.team_id === subject_id)
  if (!t) return null
  return { name: t.name, photo_url: t.logo.thumbnail }
}

function Ring({
  metric,
  rawValue,
  statistic,
  neutral,
}: {
  metric: Metric
  rawValue: number
  statistic: string
  neutral: boolean
}) {
  const thresholdColor = useFmtColor({
    metric: metric.key,
    component: 'percent',
    statistic,
    value: rawValue,
  })
  const color = neutral ? NEUTRAL_COLOR : thresholdColor
  return (
    <RingProgressCard
      title={metric.short_name || metric.name}
      value={rawValue * 100}
      color={color}
      size={RING_SIZE_PX}
      valueLabel={
        <Fmt
          metric={metric.key}
          component="percent"
          statistic={statistic}
          value={rawValue}
        />
      }
    />
  )
}

function Inner({ settings }: { settings: Settings }) {
  const { data: allMetrics } = useMetrics()
  const metric =
    allMetrics.find((m) => m.key === settings.metric_key) ||
    allMetrics.find((m) => m.is_kpi)

  if (!metric) return <WidgetEmpty message="Pick a KPI" />
  if (settings.subject_id <= 0) return <WidgetEmpty message="Pick a subject" />
  return <InnerLoaded metric={metric} settings={settings} />
}

function InnerLoaded({
  metric,
  settings,
}: {
  metric: Metric
  settings: Settings
}) {
  const ctx = useResolvedContext(settings)
  const subject = useSubject(
    settings.subject_type,
    settings.subject_id,
    ctx.competition_id
  )

  const { data: actualPoints } = useMetricsData({
    period_type: ctx.period_type,
    period_range: false,
    periods: [ctx.period],
    subject_type: ctx.subject_type,
    subject_ids: [ctx.subject_id],
    metrics: [metric.key],
    components: ['actual'],
    statistics: [settings.statistic],
    competition_id: ctx.competition_id,
  })

  const { data: percentPoints } = useMetricsData({
    period_type: ctx.period_type,
    period_range: false,
    periods: [ctx.period],
    subject_type: ctx.subject_type,
    subject_ids: [ctx.subject_id],
    metrics: [metric.key],
    components: ['percent'],
    statistics: [settings.statistic],
    competition_id: ctx.competition_id,
  })

  if (!subject) return <WidgetEmpty message="Pick a subject" />

  const actualValue = actualPoints[0]?.value ?? null
  const percentValue = percentPoints[0]?.value ?? null

  return (
    <div className={classes.box}>
      <div className={classes.left}>
        <div className={classes.header}>
          <Image
            src={subject.photo_url}
            alt={subject.name}
            fit="cover"
            className={classes.avatar}
          />
          <div className={classes.name}>{subject.name}</div>
        </div>
        <div className={classes.body}>
          {settings.title && (
            <div className={classes.label}>{settings.title}</div>
          )}
          <div className={classes.value}>
            <FmtColored
              metric={metric.key}
              component="actual"
              statistic={settings.statistic}
              value={actualValue}
            />
          </div>
        </div>
      </div>
      <div className={classes.ring}>
        {percentValue != null && percentValue > 0 ? (
          <Ring
            metric={metric}
            rawValue={percentValue}
            statistic={settings.statistic}
            neutral={settings.use_neutral_colors}
          />
        ) : (
          <RingProgressCard
            title={metric.short_name || metric.name}
            value={0}
            size={RING_SIZE_PX}
            valueLabel="-"
          />
        )}
      </div>
    </div>
  )
}

function Component({ settings }: { settings: Settings }) {
  return (
    <WidgetSuspense>
      <Inner settings={settings} />
    </WidgetSuspense>
  )
}

function OptionsForm({
  value,
  onChange,
}: {
  value: Settings
  onChange: (next: Settings) => void
}) {
  const patch = (p: Partial<Settings>) => onChange({ ...value, ...p })
  return (
    <Stack gap="xs">
      <TitleInput value={value.title} onChange={(v) => patch({ title: v })} />
      <MetricSelect
        kpiOnly
        value={value.metric_key}
        onChange={(v) => patch({ metric_key: v })}
      />
      <StatisticSelect
        allowed={['value', 'value_ytd']}
        value={value.statistic}
        onChange={(v) =>
          patch({ statistic: (v as 'value' | 'value_ytd') || 'value' })
        }
      />
      <SubjectFields
        subjectTypes={['user', 'team']}
        subject_type={value.subject_type}
        subject_id={value.subject_id}
        onChange={(p) => {
          const next: Partial<Settings> = {}
          if (p.subject_type !== undefined) {
            if (p.subject_type === 'user' || p.subject_type === 'team') {
              next.subject_type = p.subject_type
            }
          }
          if (p.subject_id !== undefined) next.subject_id = p.subject_id
          patch(next)
        }}
      />
      <PeriodFields
        period_type={value.period_type}
        period={value.period}
        use_max_period={value.use_max_period}
        onChange={(p) => patch(p as Partial<Settings>)}
      />
      <NeutralColorsToggle
        value={value.use_neutral_colors}
        onChange={(v) => patch({ use_neutral_colors: v })}
      />
    </Stack>
  )
}

export const KpiSpotlightWidget: DashboardWidget<Settings> = {
  type: 'kpi_spotlight',
  label: 'KPI Spotlight',
  description: 'Subject photo with KPI value and percent ring',
  icon: IconUserStar,
  minW: 2,
  minH: 1,
  maxW: 2,
  maxH: 1,
  schema: Schema,
  defaultSettings: Schema.parse({}),
  Component,
  OptionsForm,
}
